<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="sc-header">
        <span class="sc-bar sc-bar--xl"></span>
        <span class="sc-bar sc-bar--md"></span>
      </div>
      <div class="sc-bar-stack">
        <div
          v-for="(seg, i) in segments"
          :key="seg.label"
          class="sc-segment"
          :style="{ '--pct': seg.pct + '%', '--i': i }"
        >
          <span class="material-symbols-rounded sc-icon">{{ seg.icon }}</span>
          <span class="sc-segment-label">{{ seg.pct }}%</span>
        </div>
      </div>
      <div class="sc-legend">
        <span
          v-for="seg in segments"
          :key="seg.label"
          class="sc-legend-item"
        >
          <span class="sc-swatch" :class="`--${seg.label}`"></span>
          {{ seg.label }}
        </span>
      </div>
    </template>

    <template #audience>
      <span class="sc-phone-q"></span>
      <div class="sc-phone-hint">
        Quick pulse:
      </div>
      <button
        v-for="(seg, i) in segments"
        :key="seg.label"
        type="button"
        class="sc-phone-btn"
        :class="[`--${seg.label}`, { '--tapped': i === tapIndex }]"
        tabindex="-1"
      >
        <span class="material-symbols-rounded sc-phone-icon">{{ seg.icon }}</span>
        <span class="sc-bar sc-bar--md"></span>
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

type Seg = { label: 'positive' | 'neutral' | 'negative'; icon: string; pct: number }

export default Vue.extend({
  name: 'PreviewSentimentCheck',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
  data() {
    return {
      segments: [
        { label: 'positive', icon: 'thumb_up', pct: 58 },
        { label: 'neutral', icon: 'horizontal_rule', pct: 27 },
        { label: 'negative', icon: 'thumb_down', pct: 15 },
      ] as Seg[],
      tapIndex: 0,
    }
  },
})
</script>

<style scoped lang="scss">
.sc-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.sc-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); }
.sc-bar--md { width: 45%; }

.sc-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.sc-bar-stack {
  display: flex; height: 44px;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(15, 15, 20, 0.05);
  margin: 6px 0;
}
.sc-segment {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  width: 0;
  color: white;
  font-size: 11px;
  font-weight: 700;
  animation: sc-grow 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--i) * 120ms + 500ms);
  overflow: hidden;
}
.sc-segment:nth-child(1) { background: var(--accent); }
.sc-segment:nth-child(2) { background: rgba(15, 15, 20, 0.25); }
.sc-segment:nth-child(3) { background: color-mix(in srgb, var(--accent) 50%, black); }
.sc-icon { font-size: 14px; }

.sc-legend {
  display: flex; gap: 12px; flex-wrap: wrap;
  font-size: 10px; font-weight: 600;
  color: rgba(15, 15, 20, 0.6);
}
.sc-legend-item {
  display: flex; align-items: center; gap: 4px;
}
.sc-swatch {
  width: 8px; height: 8px;
  border-radius: 2px;
}
.sc-swatch.--positive { background: var(--accent); }
.sc-swatch.--neutral { background: rgba(15, 15, 20, 0.25); }
.sc-swatch.--negative { background: color-mix(in srgb, var(--accent) 50%, black); }

/* phone */
.sc-phone-q {
  display: block; height: 8px; width: 70%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 2px;
}
.sc-phone-hint {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.sc-phone-btn {
  appearance: none; background: white;
  display: grid;
  grid-template-columns: 22px 1fr;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(15, 15, 20, 0.08);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}
.sc-phone-icon {
  width: 22px; height: 22px;
  border-radius: 999px;
  display: grid; place-items: center;
  font-size: 13px;
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
}
.sc-phone-btn.--tapped {
  background: color-mix(in srgb, var(--accent) 10%, white);
  border-color: color-mix(in srgb, var(--accent) 60%, transparent);
  animation: sc-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.sc-phone-btn.--positive.--tapped .sc-phone-icon { background: var(--accent); color: white; }
.sc-phone-btn.--neutral.--tapped .sc-phone-icon { background: rgba(15, 15, 20, 0.4); color: white; }
.sc-phone-btn.--negative.--tapped .sc-phone-icon { background: color-mix(in srgb, var(--accent) 50%, black); color: white; }

@keyframes sc-grow {
  0%, 12%   { width: 0; }
  60%       { width: var(--pct, 33%); }
  92%, 100% { width: var(--pct, 33%); }
}
@keyframes sc-tap {
  0%, 4%  { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  18%     { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%    { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .sc-segment { animation: none; width: var(--pct, 33%); }
  .sc-phone-btn.--tapped { animation: none; }
}
</style>
